import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TestimonialsCarousel() {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    if (!autoPlay || testimonials.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [autoPlay, testimonials.length]);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToNext = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToPrev = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index) => {
    setAutoPlay(false);
    setCurrentIndex(index);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
        strokeWidth={1.5}
      />
    ));
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  if (testimonials.length === 0) {
    return null; // Don't show section if no testimonials
  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="relative">
      {/* Main Testimonial Card */}
      <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.08)] p-8 sm:p-12 relative overflow-hidden">
        {/* Quote Icon Background */}
        <div className="absolute top-6 right-6 opacity-5">
          <Quote className="w-32 h-32 text-blue-600" strokeWidth={1} />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Stars */}
          <div className="flex items-center gap-1 mb-6 justify-center sm:justify-start">
            {renderStars(currentTestimonial.rating)}
          </div>

          {/* Review Text */}
          <blockquote className="text-lg sm:text-xl text-slate-700 leading-relaxed mb-8 italic">
            "{currentTestimonial.review}"
          </blockquote>

          {/* Author Info */}
          <div className="flex items-center gap-4">
            {/* Avatar Placeholder */}
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-md">
              {currentTestimonial.name.charAt(0).toUpperCase()}
            </div>

            <div>
              <div className="font-bold text-slate-900">{currentTestimonial.name}</div>
              <div className="text-sm text-slate-600">{currentTestimonial.role}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      {testimonials.length > 1 && (
        <>
          {/* Arrow Buttons */}
          <button
            onClick={goToPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-white rounded-full p-3 shadow-lg border-2 border-slate-100 hover:bg-slate-50 hover:border-slate-200 transition-all active:scale-95 z-20"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6 text-slate-700" strokeWidth={2} />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 bg-white rounded-full p-3 shadow-lg border-2 border-slate-100 hover:bg-slate-50 hover:border-slate-200 transition-all active:scale-95 z-20"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6 text-slate-700" strokeWidth={2} />
          </button>

          {/* Dot Indicators */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-blue-600 w-8'
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default TestimonialsCarousel;
